import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { X } from "lucide-react";

const norm = (r) => (r === "admin" ? "owner" : r === "cashier" ? "associate" : r);

const STEPS = {
  owner: [
    { title: "Welcome to your store", body: "This admin panel is your control center. Everything on the website — products, prices, orders, staff — is managed from here. This quick tour shows once; re-read anything later in Help & How-To." },
    { title: "Products & Quick Add", body: "Add items 3 ways: WhatsApp a photo + caption to the bot, use Quick Add (drop photos, AI fills the details), or Products → Bulk import to upload a whole CSV. You can also Export CSV to back up or edit your inventory in a spreadsheet." },
    { title: "Orders & delivery", body: "Orders shows every web, POS and payment-link sale. Schedule deliveries (Tue/Thu/Sat) from the expanded order — the customer is texted/emailed automatically. Hold orders ($50 deposit) can't be scheduled until the balance is collected." },
    { title: "Reports — owner only", body: "Reports shows revenue, margins and your product costs. Only you see cost data — managers get daily totals with no cost/margin. Add costs on products (or bulk via CSV) to make margins accurate." },
    { title: "POS & payment links", body: "POS · Counter turns any device into a register: cash, tap-to-pay via Square, or text the customer a secure payment link. Discounts above a staff member's limit require your manager PIN (set in Staff & Roles)." },
    { title: "Staff & guardrails", body: "Create logins in Staff & Roles: managers (10% discounts, daily reports), associates (register only, no discounts without a PIN), drivers (delivery app only, never collect money). Every sensitive action lands in the Audit Log." },
  ],
  manager: [
    { title: "Welcome, manager", body: "You run the floor: orders, the register, and deliveries. This quick tour shows once — the Help & How-To page has the full guides." },
    { title: "Your discount authority", body: "You can approve up to 10% on single items and 15% on bundles (3+ items) at the POS — either on your own sales or by entering your PIN when an associate asks. Anything bigger needs the owner." },
    { title: "Orders & deliveries", body: "In Orders, schedule/reschedule deliveries (Tue/Thu/Sat — customer gets notified automatically). Orders that aren't fully paid (holds, unpaid payment links) can't be scheduled until the balance is collected." },
    { title: "Daily numbers", body: "Reports gives you today's totals and order counts. Cost and margin data is owner-only by design." },
  ],
  associate: [
    { title: "Welcome to the register", body: "The POS is your home screen. Tap products to build a cart, then take cash, card (tap-to-pay), or text the customer a payment link. This tour shows once — the ? Help button is always in the top bar." },
    { title: "Cash & card", body: "Cash: type what the customer handed you and the screen shows the change to give back. Card: the Square app opens for the tap — hand the device to the customer." },
    { title: "No discounts without a PIN", body: "You can't apply discounts on your own. If a customer asks, call a manager over — they enter their 4-digit PIN right on your screen to approve it. Never work around this." },
    { title: "Holds & help", body: "A customer can reserve items with a $50 deposit for 7 days (done at online checkout). If anything looks wrong, stop and call the store: (616) 410-0158." },
  ],
  driver: [
    { title: "Your delivery day", body: "Your stops for today load automatically with Directions and Call buttons. This tour shows once — the ? Help button stays in the header." },
    { title: "You never collect money", body: "Every order in your app is already paid in full. If a customer offers payment, politely decline and tell them to call the store." },
    { title: "The 3-tap flow", body: "Start delivery when you leave (customer gets a text) → Arrived at the door → Mark DELIVERED: customer signs on your screen and you confirm no damage (or photo the damage)." },
    { title: "Nobody home?", body: "Tap \"Customer not home / refused\" — the store is alerted and reschedules. Never leave items unattended." },
  ],
};

export default function RoleWalkthrough() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const role = norm(user?.role);
  const steps = STEPS[role];
  const key = user ? `ipv_walkthrough_v1:${user.id || user.email}` : null;

  useEffect(() => {
    if (user && steps && key && !localStorage.getItem(key)) {
      setOpen(true);
      setStep(0);
    }
  }, [user, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        localStorage.setItem(key, "1");
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, key]);

  if (!open || !steps) return null;
  const dismiss = () => {
    localStorage.setItem(key, "1");
    setOpen(false);
  };
  const s = steps[step];
  const last = step === steps.length - 1;

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="walkthrough-modal" onClick={dismiss}>
      <div className="bg-white w-full max-w-md rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0A1C40] text-white px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC220] font-bold">
              Getting started · {role}
            </div>
            <div className="font-display font-black text-xl mt-1" data-testid="walkthrough-title">{s.title}</div>
          </div>
          <button onClick={dismiss} className="opacity-70 hover:opacity-100 mt-1" data-testid="walkthrough-skip" aria-label="Skip tour">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm leading-relaxed text-gray-800" data-testid="walkthrough-body">{s.body}</p>
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <span key={i} className={`w-2 h-2 rounded-full ${i === step ? "bg-[#0A1C40]" : "bg-gray-300"}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="text-sm font-bold text-gray-600 px-3 py-2" data-testid="walkthrough-back">
                  Back
                </button>
              )}
              <button
                onClick={() => (last ? dismiss() : setStep(step + 1))}
                className="bg-[#0A1C40] text-white text-sm font-bold px-5 py-2 rounded-full hover:bg-black transition-colors"
                data-testid={last ? "walkthrough-done" : "walkthrough-next"}
              >
                {last ? "Got it — let's go" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
